import { z } from 'zod';
import { dansLOrdre } from '@/shared/referentiel/chantier';
import { estMetier, type Metier } from '@/shared/referentiel/etapes';
import { nomContact } from '@/shared/referentiel/fiches';
import type {
  Affectation,
  Chantier,
  EntreeHistorique,
  EtapeChantier,
  FicheChantier,
  ModeleEtape,
  SaisieEtape,
  TypeDocument,
  Preuve,
  Reserve,
  ResumeChantier,
  ResumeEtape,
  SaisieChantier,
} from '../types';
import {
  COLONNES_CONTACT,
  COLONNES_FICHIER,
  COLONNES_POSITION,
  COLONNES_SITE,
  type ColonnesPosition,
  champsFichier,
  champsPosition,
  ligneContact,
  ligneSite,
  messageErreur,
  versContact,
  versFichier,
  versPosition,
  versSite,
} from './lignes';

/*
 * Les chantiers tels que les renvoie PostgREST (db/003-chantiers.sql), vérifiés
 * avant usage. Pur, comme lignes.ts : ni réseau ni client Supabase.
 */

// ── Sélections ───────────────────────────────────────────────────────────────

const COLONNES_CHANTIER =
  'id, reference, entreprise_id, site_id, titre, batiment, notes, cree_le, cloture_le, partenaire_id, partenaires(raison_sociale)';
const COLONNES_ETAPE =
  'id, chantier_id, ordre, libelle, metier, icone, finale, partenaire_id, debut_prevu, fin_prevue, heure_debut, heure_fin, statut, demarree_le, soumise_le, validee_le, validee_par_nom, motif_reouverture, reserve_texte, reserve_le, reserve_par_nom, photos_min, documents, en_attente_de, partenaires(raison_sociale), releves(complet)';
const COLONNES_PREUVE = `${COLONNES_FICHIER}, etape_id, type`;

/** Le chantier seul, avec les noms qu'affiche une ligne de liste. */
export const SELECTION_CHANTIER = `${COLONNES_CHANTIER}, entreprises(raison_sociale), sites(nom, commune, departement, ${COLONNES_POSITION})`;

/** Une requête pour la liste : les preuves sont comptées par la base, pas rapatriées. */
export const SELECTION_RESUME_CHANTIER = `${SELECTION_CHANTIER}, etapes_chantier(${COLONNES_ETAPE}, preuves(count))`;

/* Le site entier et les contacts de l'entreprise : pour un partenaire, la base
   ne laisse passer que les contacts « sur place ». */
export const SELECTION_FICHE_CHANTIER = `${COLONNES_CHANTIER}, entreprises(raison_sociale, siret, activite, adresse, code_postal, commune, contacts(${COLONNES_CONTACT})), sites(${COLONNES_SITE}), etapes_chantier(${COLONNES_ETAPE}, preuves(${COLONNES_PREUVE}))`;

export const COLONNES_MODELE =
  'id, ordre, libelle, metier, icone, finale, photos_min, documents, attend';
export const COLONNES_TYPE_DOCUMENT = 'id, libelle';

export const COLONNES_HISTORIQUE =
  'id, horodatage, acteur_nom, action, objet_type, libelle, changements';

// ── Lignes ───────────────────────────────────────────────────────────────────

/* Un métier inconnu (ajouté en base avant l'écran) se lit « autre » plutôt que de tout bloquer. */
const metier = z.string().transform((v): Metier => (estMetier(v) ? v : 'autre'));
const statutEtape = z.enum([
  'a-planifier',
  'planifiee',
  'en-cours',
  'a-valider',
  'validee',
  'rouverte',
]);
const typePreuve = z.string();

const ligneChantier = z.object({
  id: z.string(),
  reference: z.string(),
  entreprise_id: z.string(),
  site_id: z.string(),
  titre: z.string(),
  batiment: z.enum(['existant', 'construction']),
  notes: z.string(),
  cree_le: z.string(),
  /* Tenue par la base (db/011-archives.sql), jamais écrite par le client. */
  cloture_le: z.string().nullable(),
  /* Le titulaire (db/017-titulaire.sql) ; un partenaire ne lit que sa propre fiche. */
  partenaire_id: z.string().nullable().default(null),
  partenaires: z.object({ raison_sociale: z.string() }).nullable().default(null),
});

export const ligneChantierSeul = ligneChantier.extend({
  entreprises: z.object({ raison_sociale: z.string() }).nullable(),
  sites: z
    .object({ nom: z.string(), commune: z.string(), departement: z.string(), ...champsPosition })
    .nullable(),
});

const ligneEtape = z.object({
  id: z.string(),
  chantier_id: z.string(),
  ordre: z.number().int(),
  libelle: z.string(),
  metier,
  icone: z.string(),
  finale: z.boolean(),
  partenaire_id: z.string().nullable(),
  debut_prevu: z.string().nullable(),
  fin_prevue: z.string().nullable(),
  statut: statutEtape,
  demarree_le: z.string().nullable(),
  soumise_le: z.string().nullable(),
  en_attente_de: z.string().nullable().default(null),
  heure_debut: z.string().nullable().default(null),
  heure_fin: z.string().nullable().default(null),
  validee_le: z.string().nullable(),
  validee_par_nom: z.string().nullable(),
  motif_reouverture: z.string().nullable(),
  reserve_texte: z.string().nullable(),
  reserve_le: z.string().nullable(),
  reserve_par_nom: z.string().nullable(),
  photos_min: z.number().int(),
  documents: z.array(z.string()),
  partenaires: z.object({ raison_sociale: z.string() }).nullable(),
  /* Un relevé par étape de visite : un objet, ou une liste selon ce que PostgREST déduit. */
  releves: z
    .union([z.object({ complet: z.boolean() }), z.array(z.object({ complet: z.boolean() }))])
    .nullable()
    .default(null),
});

const lignePreuve = z.object({ ...champsFichier, etape_id: z.string(), type: typePreuve });

export const ligneResumeChantier = ligneChantierSeul.extend({
  etapes_chantier: z.array(
    ligneEtape.extend({ preuves: z.array(z.object({ count: z.coerce.number() })) })
  ),
});

export const ligneFicheChantier = ligneChantier.extend({
  entreprises: z
    .object({
      raison_sociale: z.string(),
      siret: z.string().default(''),
      activite: z.string().default(''),
      adresse: z.string().default(''),
      code_postal: z.string().default(''),
      commune: z.string().default(''),
      contacts: z.array(ligneContact),
    })
    .nullable(),
  sites: ligneSite,
  etapes_chantier: z.array(ligneEtape.extend({ preuves: z.array(lignePreuve) })),
});

export const ligneModele = z.object({
  id: z.string(),
  ordre: z.number().int(),
  libelle: z.string(),
  metier,
  icone: z.string(),
  finale: z.boolean(),
  photos_min: z.number().int(),
  documents: z.array(z.string()),
  attend: z.string().nullable().default(null),
});

export const ligneTypeDocument = z.object({ id: z.string(), libelle: z.string() });

export const ligneHistorique = z.object({
  /* `bigint` : nombre JSON chez PostgREST ; on accepte aussi la chaîne. */
  id: z.coerce.number(),
  horodatage: z.string(),
  acteur_nom: z.string().nullable(),
  action: z.enum(['creation', 'modification', 'suppression']),
  objet_type: z.string(),
  libelle: z.string().nullable(),
  changements: z.record(z.string(), z.tuple([z.unknown(), z.unknown()])),
});

/** Le chantier d'une étape : le dossier où ranger ses preuves. */
export const ligneChantierDEtape = z.object({ chantier_id: z.string(), statut: statutEtape });

// ── Vers le domaine ──────────────────────────────────────────────────────────

interface SiteDuChantier {
  nom: string;
  commune: string;
  departement: string;
}

function versChantierDe(
  l: z.infer<typeof ligneChantier>,
  entrepriseNom: string,
  site: (SiteDuChantier & ColonnesPosition) | null
): Chantier {
  return {
    id: l.id,
    reference: l.reference,
    entrepriseId: l.entreprise_id,
    entrepriseNom,
    siteId: l.site_id,
    siteNom: site?.nom ?? '',
    position: site === null ? null : versPosition(site),
    commune: site?.commune ?? '',
    departement: site?.departement ?? '',
    titre: l.titre,
    batiment: l.batiment,
    notes: l.notes,
    creeLe: l.cree_le,
    clotureLe: l.cloture_le,
    partenaireId: l.partenaire_id,
    partenaireNom: l.partenaires?.raison_sociale ?? null,
  };
}

export function versChantier(l: z.infer<typeof ligneChantierSeul>): Chantier {
  return versChantierDe(l, l.entreprises?.raison_sociale ?? '', l.sites);
}

/* La base pose le texte et la date ensemble (db/005-reserves.sql) : l'un sans
   l'autre ne fait pas une réserve. */
function versReserve(l: z.infer<typeof ligneEtape>): Reserve | null {
  if (l.reserve_texte === null || l.reserve_le === null) return null;
  return { texte: l.reserve_texte, signaleeLe: l.reserve_le, signaleePar: l.reserve_par_nom };
}

function versEtapeSansPreuves(
  l: z.infer<typeof ligneEtape>
): Omit<EtapeChantier, 'preuves' | 'exigences'> {
  return {
    id: l.id,
    chantierId: l.chantier_id,
    ordre: l.ordre,
    libelle: l.libelle,
    metier: l.metier,
    icone: l.icone,
    finale: l.finale,
    partenaireId: l.partenaire_id,
    partenaireNom: l.partenaires?.raison_sociale ?? null,
    debutPrevu: l.debut_prevu,
    finPrevue: l.fin_prevue,
    statut: l.statut,
    demarreeLe: l.demarree_le,
    soumiseLe: l.soumise_le,
    valideeLe: l.validee_le,
    valideePar: l.validee_par_nom,
    motifReouverture: l.motif_reouverture,
    reserve: versReserve(l),
    enAttenteDe: l.en_attente_de,
    heureDebut: l.heure_debut?.slice(0, 5) ?? null,
    heureFin: l.heure_fin?.slice(0, 5) ?? null,
    releveComplet: Array.isArray(l.releves)
      ? (l.releves[0]?.complet ?? false)
      : (l.releves?.complet ?? false),
    releveCommence: Array.isArray(l.releves) ? l.releves.length > 0 : l.releves !== null,
  };
}

function versPreuve(l: z.infer<typeof lignePreuve>): Preuve {
  return { ...versFichier(l), etapeId: l.etape_id, type: l.type };
}

export function versResumeChantier(l: z.infer<typeof ligneResumeChantier>): ResumeChantier {
  const etapes: ResumeEtape[] = l.etapes_chantier.map((e) => ({
    ...versEtapeSansPreuves(e),
    nbPreuves: e.preuves.reduce((total, p) => total + p.count, 0),
  }));
  return { ...versChantier(l), etapes: dansLOrdre(etapes) };
}

export function versFicheChantier(
  l: z.infer<typeof ligneFicheChantier>,
  typesDocument: TypeDocument[]
): FicheChantier {
  const etapes: EtapeChantier[] = l.etapes_chantier.map((e) => ({
    ...versEtapeSansPreuves(e),
    exigences: { photosMin: e.photos_min, documents: [...e.documents] },
    /* Dans l'ordre du dépôt : la galerie se lit comme le chantier s'est passé. */
    preuves: e.preuves.map(versPreuve).sort((a, b) => a.ajouteLe.localeCompare(b.ajouteLe)),
  }));
  const surPlace =
    l.entreprises?.contacts
      .filter((c) => c.sur_place)
      .map(versContact)
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))[0] ?? null;
  const e = l.entreprises;
  /* Le dirigeant : le contact dont la fonction le dit (un partenaire ne voit que ceux « sur place »). */
  const dirigeant = e?.contacts
    .map(versContact)
    .find((c) =>
      /g[ée]rant|dirige|pr[ée]sident|directeur|chef d.entreprise|exploitant/i.test(c.fonction)
    );
  return {
    ...versChantierDe(l, e?.raison_sociale ?? '', l.sites),
    etapes: dansLOrdre(etapes),
    typesDocument,
    site: versSite(l.sites),
    contactSurPlace: surPlace,
    ...(e
      ? {
          entreprise: {
            siret: e.siret,
            activite: e.activite,
            adresse: e.adresse,
            codePostal: e.code_postal,
            commune: e.commune,
            dirigeant: dirigeant ? nomContact(dirigeant) : '',
          },
        }
      : {}),
  };
}

/** Le modèle, dans l'ordre de la chaîne, la réception du chantier en dernier. */
export function versModele(lignes: z.infer<typeof ligneModele>[]): ModeleEtape[] {
  return dansLOrdre(
    lignes.map((l) => ({
      id: l.id,
      ordre: l.ordre,
      libelle: l.libelle,
      metier: l.metier,
      icone: l.icone,
      finale: l.finale,
      exigences: { photosMin: l.photos_min, documents: [...l.documents] },
      attend: l.attend,
    }))
  );
}

export function versEntreeHistorique(l: z.infer<typeof ligneHistorique>): EntreeHistorique {
  return {
    id: l.id,
    horodatage: l.horodatage,
    acteurNom: l.acteur_nom,
    action: l.action,
    objetType: l.objet_type,
    libelle: l.libelle,
    changements: l.changements,
  };
}

// ── Vers la base ─────────────────────────────────────────────────────────────

export function ligneDeSaisieChantier(s: SaisieChantier) {
  return {
    entreprise_id: s.entrepriseId,
    site_id: s.siteId,
    titre: s.titre.trim(),
    batiment: s.batiment,
    notes: s.notes,
  };
}

/** Les colonnes qu'écrit le bureau pour une étape du modèle. */
export function ligneDeModele(s: SaisieEtape) {
  return {
    libelle: s.libelle.trim(),
    metier: s.metier,
    icone: s.icone,
    photos_min: s.exigences.photosMin,
    documents: [...new Set(s.exigences.documents)],
    ...(s.attend === undefined ? {} : { attend: s.attend }),
  };
}

/** Les arguments de `ajouter_etape` (db/009-chaine.sql). */
export function argumentsAjoutEtape(chantierId: string, s: SaisieEtape) {
  return {
    p_chantier: chantierId,
    p_libelle: s.libelle.trim(),
    p_metier: s.metier,
    p_icone: s.icone,
    p_photos: s.exigences.photosMin,
    p_documents: [...new Set(s.exigences.documents)],
  };
}

/** Les arguments de `affecter_etape` (db/027-affecter-avec-heures.sql). */
export function argumentsAffectation(etapeId: string, a: Affectation) {
  return {
    p_etape: etapeId,
    p_partenaire: a.partenaireId,
    p_debut: a.debutPrevu,
    p_fin: a.finPrevue,
    ...(a.heures ? { p_heure_debut: a.heures.debut, p_heure_fin: a.heures.fin } : {}),
  };
}

/**
 * Le filtre PostgREST de l'historique d'un chantier : le chantier lui-même, ses
 * étapes (rattachées par `parent_id`) et les preuves de ces étapes, supprimées
 * comprises. Les identifiants sont des uuid : rien à échapper.
 */
export function filtreHistorique(chantierId: string, etapeIds: readonly string[]): string {
  const clauses = [
    `and(objet_type.eq.chantiers,objet_id.eq.${chantierId})`,
    `and(objet_type.eq.etapes_chantier,parent_id.eq.${chantierId})`,
  ];
  if (etapeIds.length > 0) {
    clauses.push(`and(objet_type.eq.preuves,parent_id.in.(${etapeIds.join(',')}))`);
  }
  return clauses.join(',');
}

/**
 * Le filtre PostgREST de la liste par défaut : les chantiers ouverts, et ceux
 * clôturés depuis `seuil`. Les guillemets protègent les « : » et « . » de la date.
 */
export function filtreSansArchives(seuil: string): string {
  return `cloture_le.is.null,cloture_le.gte."${seuil}"`;
}

// ── Erreurs ──────────────────────────────────────────────────────────────────

/** Le code des refus métier levés par les fonctions de db/003-chantiers.sql et suivants. */
export const CODE_REFUS = 'VL001';

/**
 * Un refus de nos fonctions (« Il manque : 2 photos. ») se montre tel quel : il
 * est écrit pour l'écran, dans les mots de chantier.ts. Toute autre erreur passe
 * par `messageErreur` et ne montre jamais le texte brut de la base.
 */
export function messageRefus(
  erreur: { code?: string | undefined; message?: string | undefined } | null | undefined,
  defaut: string,
  propres: Readonly<Record<string, string>> = {}
): string {
  const message = erreur?.message?.trim() ?? '';
  if (erreur?.code === CODE_REFUS && message !== '') return message;
  return messageErreur(erreur, defaut, propres);
}
